import * as v from "valibot";
import type { ApiSchema } from "../api/schema.ts";

/**
 * Api schema for tender-package-service's /contract resource root
 * (business contracts between a buyer and its suppliers). Imported by the
 * contractV2 fat lambda and the frontend's typed client — both derive their
 * types from the schemas here.
 *
 * Routes are added here as they are implemented on the fat lambda; the
 * entity schemas are the WIRE shapes those responses embed — not the
 * service's stored items, which stay private to the service.
 */

// A contract: the stored item minus the Dynamo key fields.
export const ContractSchema = v.object({
  id: v.string(),
  name: v.string(),
  createdDate: v.string(),
  authorId: v.string(),
  tenderPackageId: v.optional(v.string()),
  buyerOrganisationId: v.string(),
  buyerWorkspaceId: v.string(),
  startDate: v.optional(v.string()),
  endDate: v.optional(v.string()),
  value: v.optional(v.number()),
  contractUrl: v.optional(v.string()),
});
export type Contract = v.InferOutput<typeof ContractSchema>;

// A subcontractor a supplier has named on a contract. The description is what
// they do on this contract, so it varies between contracts for one company.
export const ContractSubcontractorSchema = v.object({
  id: v.string(),
  name: v.string(),
  description: v.string(),
});
export type ContractSubcontractor = v.InferOutput<
  typeof ContractSubcontractorSchema
>;

// A contract with the supplier organisations party to it, each carrying the
// subcontractors it has named.
export const ContractWithSuppliersSchema = v.object({
  ...ContractSchema.entries,
  suppliers: v.array(
    v.object({
      organisationId: v.string(),
      workspaceId: v.string(),
      name: v.string(),
      subcontractors: v.array(ContractSubcontractorSchema),
    }),
  ),
});
export type ContractWithSuppliers = v.InferOutput<
  typeof ContractWithSuppliersSchema
>;

// A user assigned to a contract.
export const ContractMemberSchema = v.object({
  userId: v.string(),
  name: v.string(),
  email: v.string(),
  organisationType: v.picklist(["BUYER", "SUPPLIER"]),
  organisationId: v.string(),
  workspaceId: v.string(),
  authorId: v.string(),
  createdDate: v.string(),
});
export type ContractMember = v.InferOutput<typeof ContractMemberSchema>;

const ListContractMembersBodySchema = v.object({
  contractId: v.string(),
});

const ContractMemberBodySchema = v.object({
  contractId: v.string(),
  userId: v.string(),
});

const AddContractMemberBodySchema = v.object({
  ...ContractMemberBodySchema.entries,
  name: v.string(),
  email: v.string(),
});

const AddContractSubcontractorBodySchema = v.object({
  contractId: v.string(),
  subcontractor: v.union([
    v.object({ id: v.string() }),
    v.object({ name: v.string() }),
  ]),
  description: v.string(),
});

const RemoveContractSubcontractorBodySchema = v.object({
  contractId: v.string(),
  subcontractorId: v.string(),
});

const CreateContractBodySchema = v.object({
  name: v.string(),
  assessmentPackageId: v.optional(v.string()),
});

const TransferContractBodySchema = v.object({
  contractId: v.string(),
  workspaceId: v.string(),
  recipients: v.exactOptional(v.array(v.string())),
});

// Only the buyer-owned details; null clears an optional field
const UpdateContractBodySchema = v.object({
  contractId: v.string(),
  name: v.exactOptional(v.string()),
  startDate: v.exactOptional(v.nullable(v.string())),
  endDate: v.exactOptional(v.nullable(v.string())),
  value: v.exactOptional(v.nullable(v.number())),
  contractUrl: v.exactOptional(v.nullable(v.string())),
});
export type UpdateContractBody = v.InferInput<typeof UpdateContractBodySchema>;

export const contractsApi = {
  list: {
    method: "GET",
    path: "/contract",
    description:
      "Lists the contracts the caller's workspace is party to, newest first. " +
      "The caller must be an organisation owner/admin or a workspace member.",
    context: "workspace",
    output: v.object({ data: v.array(ContractSchema) }),
  },
  get: {
    method: "GET",
    path: "/contract/{contractId}",
    description:
      "One contract with its suppliers. The caller's workspace must be " +
      "party to the contract.",
    context: "workspace",
    output: v.object({ data: ContractWithSuppliersSchema }),
  },
  create: {
    method: "POST",
    path: "/contract",
    description:
      "Creates the contract and its buyer link — standalone with just a " +
      "name, or from a tender package, which links the package's awarded " +
      "suppliers and so must be awarded first.",
    context: "workspace",
    input: CreateContractBodySchema,
    output: v.object({ data: ContractSchema }),
  },
  update: {
    method: "PATCH",
    path: "/contract",
    description:
      "Updates the contract's details. Only members of the buyer workspace " +
      "may update; sending null for an optional field clears it.",
    context: "workspace",
    input: UpdateContractBodySchema,
    output: v.object({ data: ContractSchema }),
  },
  transfer: {
    method: "POST",
    path: "/contract/transfer",
    description:
      "Transfers the contract to another workspace in the buyer " +
      "organisation. Buyer members keep their place only if they can access " +
      "the new workspace, as does the caller; recipients in that workspace " +
      "may be named, and at least one must be when the caller cannot access it.",
    context: "workspace",
    input: TransferContractBodySchema,
    output: v.object({ data: ContractSchema }),
  },
  listMembers: {
    method: "POST",
    path: "/contract/member/list",
    description:
      "Lists the users assigned to the contract, across its parties. The " +
      "caller's workspace must be party to the contract.",
    context: "workspace",
    input: ListContractMembersBodySchema,
    output: v.object({ data: v.array(ContractMemberSchema) }),
  },
  addMember: {
    method: "POST",
    path: "/contract/member",
    description:
      "Adds a user to the caller's party's team on the contract. " +
      "Idempotent: adding an existing member returns them unchanged.",
    context: "workspace",
    input: AddContractMemberBodySchema,
    output: v.object({ data: ContractMemberSchema }),
  },
  removeMember: {
    method: "DELETE",
    path: "/contract/member",
    description:
      "Removes a user from the contract's team. Idempotent: removing a " +
      "non-member is a no-op.",
    context: "workspace",
    input: ContractMemberBodySchema,
    output: v.object({ data: v.object({ userId: v.string() }) }),
  },
  addSubcontractor: {
    method: "POST",
    path: "/contract/subcontractor",
    description:
      "Names a subcontractor the caller's own party is using on the " +
      "contract. Only a supplier party may add, and only under its own " +
      "organisation; the buyer can read them but not add any. Passing a " +
      "name creates a record on the supplier's list, which later contracts " +
      "can then reuse by id.",
    context: "workspace",
    input: AddContractSubcontractorBodySchema,
    output: v.object({ data: ContractSubcontractorSchema }),
  },
  removeSubcontractor: {
    method: "DELETE",
    path: "/contract/subcontractor",
    description:
      "Removes a subcontractor the caller's own party named on the " +
      "contract, and with it the record on the supplier's list, since " +
      "nothing else can prune that list yet. Only a supplier party may " +
      "remove, and only under its own organisation. Idempotent: removing " +
      "one already gone is a no-op.",
    context: "workspace",
    input: RemoveContractSubcontractorBodySchema,
    output: v.object({ data: v.object({ subcontractorId: v.string() }) }),
  },
} satisfies ApiSchema;
